'use client';

/**
 * GlobalScopeBar
 *
 * A slim, always-visible bar rendered at the top of the main content area.
 * Shows the Disciplina (required) and Organizzazione (optional) selectors
 * drawn from the global OrgDisciplineContext.
 *
 * Design: compact MUI bar that doesn't crowd the page content.
 * Hidden until the user is authenticated (StaticAuthContext).
 */

import React from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Typography,
  Chip,
  Button,
} from '@mui/material';
import { useAuth } from '../contexts/StaticAuthContext';
import { useOrgDiscipline } from '../contexts/OrgDisciplineContext';
import type { SportDisciplineResponse } from '../types/sport-discipline';
import ErrorAlert from './ErrorAlert';

export default function GlobalScopeBar() {
  const { authState } = useAuth();
  const [mounted, setMounted] = React.useState(false);
  React.useEffect(() => setMounted(true), []);

  // Evita hydration mismatch: i valori dei Select provengono da localStorage/fetch
  // (solo lato client), quindi in SSR la label MUI non è "shrink" mentre sul client sì.
  // Finché non è montato renderizziamo un placeholder STABILE della stessa altezza,
  // identico tra server e primo render client (nessun mismatch, nessun layout-shift).
  if (!mounted) {
    return <div style={{ minHeight: 52 }} aria-hidden="true" />;
  }

  // Don't render for unauthenticated users
  if (!authState.isAuthenticated || !authState.user) {
    return null;
  }

  return <GlobalScopeBarInner />;
}

/** Separated so hooks always run in an authenticated context */
function GlobalScopeBarInner() {
  const {
    disciplines,
    disciplinesLoading,
    disciplinesError,
    selectedDisciplineCode,
    setSelectedDisciplineCode,
    selectedDiscipline,

    orgs,
    orgsLoading,
    selectedOrgId,
    setSelectedOrgId,

    contextRejection,
    resetRejectedContext,
  } = useOrgDiscipline();

  const { authState } = useAuth();

  // Chi puo' cambiare organizzazione:
  //  - l'amministratore di sistema, che le vede tutte;
  //  - chi ha un ruolo in piu' di un'associazione.
  // Con una sola associazione non c'e' nulla da scegliere, e mostrare un menu
  // con una voce sola suggerisce un'alternativa che non esiste.
  //
  // Sul sottodominio tenant l'organizzazione arriva dall'host: non e'
  // negoziabile (il backend rifiuta con 403 un valore diverso), quindi si
  // mostra come etichetta invece che come menu.
  const privilegio = String(authState.user?.privilege ?? '').toLowerCase();
  const organizzazioneImposta = Boolean(authState.user?.scope?.locked_by_host);
  const mostraSelettoreOrg =
    !organizzazioneImposta && (privilegio === 'administrator' || orgs.length > 1);

  return (
    <>
    {/* Nessun ripiego: se le discipline dell'organizzazione non si caricano,
        l'errore si vede — non si mostrano tutte le discipline come se
        l'organizzazione le praticasse tutte. */}
    {disciplinesError && (
      <Box sx={{ px: 2, pt: 1 }}>
        <ErrorAlert error={disciplinesError} />
      </Box>
    )}
    {/* Contesto salvato e non piu' valido: il motivo (del backend quando viene
        da una risposta) e la scelta di azzerarlo. Non si scarta in silenzio;
        in alternativa si sceglie un altro valore dal selettore qui sotto. */}
    {contextRejection && (
      <Box sx={{ px: 2, pt: 1 }} data-testid="context-rejected">
        <ErrorAlert
          error={contextRejection.error}
          title={contextRejection.part === 'organization'
            ? "L'organizzazione del contesto non è valida"
            : 'La disciplina del contesto non è valida'}
        />
        <Button
          size="small"
          variant="outlined"
          onClick={resetRejectedContext}
          data-testid="context-reset"
          sx={{ mt: 1 }}
        >
          {contextRejection.part === 'organization'
            ? "Azzera l'organizzazione"
            : 'Azzera la disciplina'}
        </Button>
      </Box>
    )}
    <Box
      component="div"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2,
        py: 1,
        bgcolor: 'grey.50',
        borderBottom: '1px solid',
        borderColor: 'divider',
        flexWrap: 'wrap',
        minHeight: 52,
      }}
    >
      {/* Label */}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontWeight: 600, letterSpacing: 0.5, flexShrink: 0, display: { xs: 'none', sm: 'block' } }}
      >
        Contesto:
      </Typography>

      {/* Disciplina — required */}
      <FormControl size="small" sx={{ minWidth: 180 }}>
        <InputLabel id="global-disc-label">Disciplina</InputLabel>
        <Select
          labelId="global-disc-label"
          label="Disciplina"
          value={selectedDisciplineCode ?? ''}
          onChange={(e) => setSelectedDisciplineCode(e.target.value || null)}
          disabled={disciplinesLoading || disciplines.length === 0}
          endAdornment={
            disciplinesLoading ? <CircularProgress size={14} sx={{ mr: 2 }} /> : undefined
          }
          sx={{ bgcolor: 'background.paper' }}
        >
          <MenuItem value="">
            <Typography variant="body2" color="text.secondary">
              — seleziona disciplina —
            </Typography>
          </MenuItem>
          {disciplines.map((d: SportDisciplineResponse) => (
            <MenuItem key={d.code} value={d.code}>
              {d.icon ? `${d.icon} ` : ''}{d.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Organizzazione — mostrata solo a chi ha davvero una scelta da fare */}
      {mostraSelettoreOrg && (
      <FormControl size="small" sx={{ minWidth: 200 }}>
        <InputLabel id="global-org-label">Organizzazione</InputLabel>
        <Select
          labelId="global-org-label"
          label="Organizzazione"
          value={selectedOrgId ?? ''}
          onChange={(e) => setSelectedOrgId(e.target.value || null)}
          disabled={orgsLoading}
          endAdornment={
            orgsLoading ? <CircularProgress size={14} sx={{ mr: 2 }} /> : undefined
          }
          sx={{ bgcolor: 'background.paper' }}
        >
          <MenuItem value="">
            <Typography variant="body2" color="text.secondary">
              Tutte le associazioni
            </Typography>
          </MenuItem>
          {orgs.map((org) => (
            <MenuItem key={org.id} value={org.id}>
              {org.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      )}

      {/* Organizzazione imposta dal sottodominio: si mostra, non si sceglie */}
      {organizzazioneImposta && (
        <Chip
          size="small"
          variant="outlined"
          label={orgs.find((o) => o.id === selectedOrgId)?.name ?? 'Organizzazione del dominio'}
        />
      )}

      {/* Active selection chips — quick visual confirmation */}
      {selectedDiscipline && (
        <Chip
          label={`${selectedDiscipline.icon ?? ''} ${selectedDiscipline.name}`}
          size="small"
          color="primary"
          variant="outlined"
          sx={{ flexShrink: 0 }}
        />
      )}
      {!selectedDisciplineCode && !disciplinesLoading && disciplines.length > 0 && (
        <Typography variant="caption" color="warning.main" sx={{ fontWeight: 500 }}>
          Seleziona una disciplina per operare
        </Typography>
      )}
    </Box>
    </>
  );
}
